import { ProcessCellForExportParams } from 'ag-grid-community';
import {
  type AsyncTranslators,
  type SyncTranslators,
  SetUserFeedback,
  SimpleFormatMessage,
} from 'components/secondaryMarket/shared/Clipboard/types';
import { IntlKey } from 'locales';
import { useCallback } from 'react';
import { PrimitiveType, useIntl } from 'react-intl';

type Cell<TRow> = { rowId: string; field: keyof TRow };

type SetUserFeedbackWithCell<TRow> = (cell: Cell<TRow>) => SetUserFeedback;

type Props<TRow, TSyncKeys extends keyof TRow, TAsyncKeys extends keyof TRow> = {
  asyncTranslators: AsyncTranslators<TRow, TAsyncKeys>;
  syncTranslators: SyncTranslators<TRow, TSyncKeys>;

  setUserFeedbackWithCell: SetUserFeedbackWithCell<TRow>;
  getRowId: (row: TRow) => string;
  simpleFormatMessage: SimpleFormatMessage;

  onCellPastePending?: (cell: Cell<TRow>) => void;
  onCellPasteSuccess?: (cell: Cell<TRow>) => void;
  onCellPasteFailure?: (cell: Cell<TRow>) => void;

  getMetadata?: (columnContext: unknown, data: TRow) => unknown;
};

export function useSimpleFormatMessage() {
  const { formatMessage } = useIntl();
  return useCallback(
    (key: IntlKey, values?: Record<string, PrimitiveType>) => formatMessage({ id: key }, values),
    [formatMessage],
  );
}

export function processCellFromClipboardWithShowError<
  TRow,
  TSyncKeys extends keyof TRow,
  TAsyncKeys extends keyof TRow,
>({
  setUserFeedbackWithCell,
  getRowId,
  simpleFormatMessage,
  asyncTranslators,
  syncTranslators,
  onCellPastePending,
  onCellPasteSuccess,
  onCellPasteFailure,
  getMetadata,
}: Props<TRow, TSyncKeys, TAsyncKeys>) {
  return ({ column, node, value: pastedValue }: ProcessCellForExportParams<TRow>) => {
    const field = column.getColDef().field as keyof TRow;

    if (!field || !node) {
      return;
    }

    const asyncTranslator = asyncTranslators[field as TAsyncKeys];
    const syncTranslator = syncTranslators[field as TSyncKeys];

    const rowId = getRowId(node.data as TRow);
    const setUserFeedback = setUserFeedbackWithCell({ rowId, field });
    const originalValue = node.data ? (node.data as TRow)[field] : undefined;

    if (asyncTranslator !== undefined) {
      onCellPastePending?.({ rowId, field });
      const pasteValueIntoCell = <T>(value: T) => {
        node.setDataValue(field as string, value);
      };

      asyncTranslator({
        pastedValue: pastedValue as string,
        doPasteCallback: pasteValueIntoCell,
        setEditableStateCallback: setUserFeedback,
        simpleFormatMessage,
        onPasteSuccess: () => onCellPasteSuccess?.({ rowId, field }),
        onPasteFailure: () => onCellPasteFailure?.({ rowId, field }),
      });

      return originalValue;
    } else if (syncTranslator !== undefined) {
      const metadata = getMetadata ? getMetadata(column.getColDef().context, node.data as TRow) : undefined;
      const translationResult = syncTranslator(pastedValue as string, simpleFormatMessage, metadata);
      if (translationResult.status === 'success') {
        setUserFeedback({ status: 'done' });
        onCellPasteSuccess?.({ rowId, field });
        return translationResult.value;
      } else if (translationResult.status === 'error') {
        setUserFeedback({ errors: translationResult.error, status: 'failed' });
        onCellPasteFailure?.({ rowId, field });
      }
      return originalValue;
    } else {
      return pastedValue;
    }
  };
}
